import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { useGetFormsQuery, useLocalGetLocationsQuery } from '../store/api/localApi';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import OrgFormManagementView from './forms/OrgFormManagementView';
import LocationFormView from './forms/LocationFormView';

// ── Main Forms Page ────────────────────────────────────────────────────────────

const Forms: React.FC = () => {
  const { isAdmin, isLocationAdmin, isOrgLevel } = useAuth();
  const { isInLocation, effectiveLocationId, effectiveOrgId } = useLocationContext();

  // Determine which view to show
  const showOrgView = isOrgLevel && !isInLocation;

  // Pass the active scope explicitly so the RTK Query cache key is unique
  // per org/location. Otherwise navigating between locations under the same
  // session reuses the cache entry from the previous location and the new
  // location's forms never appear (the backend honours the session header
  // either way, but the cache key is the URL).
  const queryParams =
    showOrgView && effectiveOrgId
      ? { organizationId: effectiveOrgId }
      : effectiveLocationId
      ? { locationId: effectiveLocationId }
      : undefined;
  const { data, isLoading, error } = useGetFormsQuery(queryParams);
  const forms = data?.forms || [];

  // Fetch locations for the publish modal (org view only)
  const { data: locations = [] } = useLocalGetLocationsQuery(effectiveOrgId || '', {
    skip: !showOrgView || !effectiveOrgId,
  });

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading forms...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading forms</h3>
            <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showOrgView ? (
          <OrgFormManagementView forms={forms} locations={locations} />
        ) : (
          <LocationFormView forms={forms} isAdmin={isAdmin} isLocationAdmin={isLocationAdmin} />
        )}
      </div>
    </div>
  );
};

export default Forms;
